/**
 * What the post-signing questionnaire is called on screen.
 *
 * The statutory questionnaire (regulation 7(ד) of the broker ethics regulations) has no name of
 * its own: the realtor hands a would-be buyer OR renter the answers of whoever holds the rights.
 * On a sale that is the seller; on a rental it is the LANDLORD, who was being asked to fill a
 * "שאלון מוכר". So a property offered for rent reads "שאלון בעל זכות בנכס", and everything else
 * — sales, and documents stored before the type was — keeps "שאלון מוכר".
 *
 * The type comes from the document (`sellerQuestionnaire.listedFor`, or the `…ListedFor` field of
 * the payload a screen receives). i18n surfaces pass `questionnaireLabelContext` as `context`,
 * which picks the `_rent` key; the Hebrew-only rows use the strings below.
 */
import type { ListingListedFor } from "../@types/sharedSchema";
import type { ListingsBlockListingFilter } from "../@types/signatures";

type ListedFor = ListingListedFor | null | undefined;

export const questionnaireLabelContext = (listedFor: ListedFor): "rent" | undefined =>
  listedFor === "rent" ? "rent" : undefined;

export const questionnaireName = (listedFor: ListedFor): string =>
  listedFor === "rent" ? "שאלון בעל זכות בנכס" : "שאלון מוכר";

export const questionnaireDownloadLabel = (listedFor: ListedFor): string =>
  listedFor === "rent" ? "הורדת שאלון בעל זכות בנכס" : "הורדת שאלון המוכר";

/**
 * The header over a list of questionnaires: one kind is named ("שאלוני מוכר" / "שאלוני בעל זכות
 * בנכס"), a mix of sale and rental is just "שאלונים".
 */
export const questionnaireGroupTitle = (listedFors: ListedFor[]): string => {
  const rent = listedFors.filter((listedFor) => listedFor === "rent").length;
  if (rent === 0) return "שאלוני מוכר";
  if (rent === listedFors.length) return "שאלוני בעל זכות בנכס";
  return "שאלונים";
};

/** The parts of a document (the owner's, or an office row) its name is read from. */
type NamedSignatureDocument = {
  documentKind?: string;
  templateNameSnapshot?: string;
  templateCustomerFacingNameSnapshot?: string;
  sellerQuestionnaire?: { listedFor?: ListingListedFor } | null;
  sellerQuestionnaireListedFor?: ListingListedFor;
};

/**
 * What a signature document is called on the realtor's screens: its template's name, as always —
 * except a questionnaire-only document (שאלון בלבד), which is called what it is for ITS property
 * ("שאלון מוכר" / "שאלון בעל זכות בנכס", stamped at send), never the template's internal
 * "(מוכר / משכיר)" name.
 */
export const signatureDocumentDisplayName = (doc: NamedSignatureDocument): string =>
  doc.documentKind === "ownerQuestionnaire"
    ? doc.templateCustomerFacingNameSnapshot?.trim() ||
      questionnaireName(doc.sellerQuestionnaire?.listedFor ?? doc.sellerQuestionnaireListedFor)
    : doc.templateNameSnapshot ?? "";

/**
 * At send time: the picked listing's type (absent means a sale), or — before one is picked —
 * what the listing picker offers.
 */
export const questionnaireListedForAtSend = (
  listing: { listingListedFor?: ListingListedFor | null } | undefined,
  pickerFilter: ListingsBlockListingFilter
): ListingListedFor => {
  if (listing) return listing.listingListedFor === "rent" ? "rent" : "sale";
  return pickerFilter === "rent" ? "rent" : "sale";
};
